import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import validate from 'validate.js';

import {
  Box,
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Modal,
  Select,
  Switch,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_active,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_highPrice,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_lowPrice,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_name,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBathrooms,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBedrooms,
  useRentalManagerUserPropertyFloorPlanEditWebApiHandler_squareFeet,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { useGetListingDetails, useGetTours } from '../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import accessUtils from '../../../utils/accessUtils';
import { postingConstraints } from '../../../utils/formValidation';
import typeCheckUtils from '../../../utils/typeCheckUtils';
import { getIsInputFormDisabled } from '../../MFAccordion/utils';

import { bathOptionsArray, bedOptionsArray } from '../../../../srcLegacy/shared/constants/ListingDetailsConstants';
import NotificationConstants from '../../../../srcLegacy/shared/constants/NotificationConstants';
import { DEFAULT_ERROR_MESSAGE } from '../../../constants/ErrorMessageConstants';

import TourCardEditable from '../../PostingPath/MediaContent/ListingTourFormField/components/TourCardEditable';
import TourCardEmpty from '../../PostingPath/MediaContent/ListingTourFormField/components/TourCardEmpty';

import InlineStaticAlert from '../../Alerts/InlineStaticAlert';
import { useAuth } from '../../AuthProvider';
import RMWInput from '../../RMWInput';
import FloorPlanAmenities from './FloorPlanAmenities';
import FloorPlanLayout from './FloorPlanLayout';
import FloorPlanUploadFormField from './FloorPlanUploadFormField';

const CHECKBOX = 'checkbox';
const NAME = 'name';
const NUMBER_TYPE_FIELD_LIST = ['squareFeet', 'lowPrice', 'highPrice'];

const firstFloorPlanActiveDefaultPrefillData = {
  name: 'active',
  type: CHECKBOX,
  checked: true,
  value: 'true',
};

type FloorPlanErrors = {
  name?: string;
  beds?: string;
  baths?: string;
  squareFeet?: string;
  lowPrice?: string;
  highPrice?: string;
  active?: string;
};

type Props = {
  onClose: () => void;
  floorPlanIndex: number;
};

const FloorPlanModal = ({ onClose, floorPlanIndex }: Props): JSX.Element => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { floorplans: floorplansWithTours } = useGetTours(listingAlias, listingDetails);
  const floorplans = useMemo(
    () =>
      (listingDetails?.floorPlans || []).map((fp, i) => ({
        ...fp,
        vrModel: floorplansWithTours[i]?.vrModel ?? fp.vrModel,
        externalTour: floorplansWithTours[i]?.externalTour ?? fp.externalTour,
      })),
    [listingDetails?.floorPlans, floorplansWithTours],
  );
  const floorPlan = floorplans[floorPlanIndex];
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const isFeed = listingDetails?.isFeed ?? false;
  const isPartialListingManaged = listingDetails?.partialListing?.isPartialListingManaged ?? false;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);
  const is3dTourDisabled = isFeed
    ? listingDetails?.isArchived || listingDetails?.restrictions?.isUpdateListingRestricted
    : isInputFormDisabled;

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  const { mutateAsync: updateName } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_name,
    successToastMessage: 'Name updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update floor plan name' },
    },
  });

  const { mutateAsync: updateBeds } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBedrooms,
    successToastMessage: 'Number of beds updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update number of beds' },
    },
  });

  const { mutateAsync: updateBaths } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_numBathrooms,
    successToastMessage: 'Number of baths updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update number of baths' },
    },
  });

  const { mutateAsync: updateSquareFeet } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_squareFeet,
    successToastMessage: 'Square footage updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update square footage' },
    },
  });

  const { mutateAsync: updateLowPrice } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_lowPrice,
    successToastMessage: 'Minimum base rent updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update minimum base rent' },
    },
  });

  const { mutateAsync: updateHighPrice } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_highPrice,
    successToastMessage: 'Maximum base rent updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update maximum base rent' },
    },
  });

  const { mutateAsync: updateActive } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanEditWebApiHandler_active,
    successToastMessage: 'Active status updated',
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'rental-manager', details: 'update active status' },
    },
  });

  const fieldMutationMap = {
    name: { mutate: updateName, param: 'name' },
    beds: { mutate: updateBeds, param: 'numBedrooms' },
    baths: { mutate: updateBaths, param: 'numBathrooms' },
    squareFeet: { mutate: updateSquareFeet, param: 'squareFeet' },
    lowPrice: { mutate: updateLowPrice, param: 'lowPrice' },
    highPrice: { mutate: updateHighPrice, param: 'highPrice' },
    active: { mutate: updateActive, param: 'active' },
  };

  const [name, setName] = useState<string>(floorPlan?.name || '');
  const [beds, setBeds] = useState<string | number>(floorPlan?.numBedrooms ?? '');
  const [baths, setBaths] = useState<string | number>(floorPlan?.numBathrooms ?? '');
  const [squareFeet, setSquareFeet] = useState<string | number>(floorPlan?.squareFeet ?? '');
  const [lowPrice, setLowPrice] = useState<string | number>(floorPlan?.lowPrice ?? '');
  const [highPrice, setHighPrice] = useState<string | number>(floorPlan?.highPrice ?? '');
  const [active, setActive] = useState<boolean>(floorPlan?.active ?? false);

  const [floorPlanErrors, setFloorPlanErrors] = useState<FloorPlanErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCloseClicked, setIsCloseClicked] = useState(false);

  const prevStateRef = useRef({
    value: '',
    errorMessage: '',
    initialRender: true,
    isModified: false,
  });

  useEffect(() => {
    if (!isMultifamily) {
      logError({
        error: new Error('ERROR@ FLOOR PLAN MODAL RENDERING — SF'),
        errorType: 'FloorPlanError',
        context: { listingAlias },
      });
    }
  }, [isMultifamily, listingAlias]);

  // ZRM-10234: very first floor plan must be `available` by default
  useEffect(() => {
    if (!isSaving && floorplans.length <= 1 && !active && name && (beds || beds === 0) && baths) {
      const mockEvent = {
        target: firstFloorPlanActiveDefaultPrefillData,
      };

      save(mockEvent);
    }
  }, [name, beds, baths, isSaving]);

  useEffect(() => {
    if (prevStateRef.current.isModified) {
      return;
    }

    if (prevStateRef.current.initialRender) {
      prevStateRef.current.initialRender = false;
    } else {
      prevStateRef.current.isModified = true;
    }
  }, [floorplans]);

  useEffect(() => {
    if (!isSaving && isCloseClicked) {
      // ZRM-10101: if either lowPrice or highPrice is missing, normalize with the other existing value
      if ((lowPrice && !highPrice) || (highPrice && !lowPrice)) {
        const mockEvent = {
          target: {
            name: lowPrice ? 'highPrice' : 'lowPrice',
            value: String(lowPrice || highPrice),
          },
        };

        save(mockEvent);
      }

      onClose();
    }
  }, [isSaving, isCloseClicked, lowPrice, highPrice, onClose]);

  const hasErrorOrNoChange = ({ name: fieldName, value }: { name: string; value: string }) => {
    if (prevStateRef.current.value === value) {
      if (prevStateRef.current.errorMessage) {
        setFloorPlanErrors({ ...floorPlanErrors, [fieldName]: prevStateRef.current.errorMessage });
      }

      return true;
    }

    if (fieldName === NAME) {
      const floorPlanNameError = validate({ floorPlanName: value }, { floorPlanName: postingConstraints.floorPlanName })
        ?.floorPlanName?.[0];

      if (floorPlanNameError) {
        setFloorPlanErrors({ ...floorPlanErrors, [fieldName]: floorPlanNameError });

        return true;
      }
    }

    if (NUMBER_TYPE_FIELD_LIST.includes(fieldName) && value !== '') {
      const errorMessage = 'Enter only whole numbers';
      const numericValue = typeCheckUtils.Number(value);

      if (!numericValue && numericValue !== 0) {
        setFloorPlanErrors({ ...floorPlanErrors, [fieldName]: errorMessage });

        return true;
      }

      if (!Number.isInteger(numericValue)) {
        setFloorPlanErrors({ ...floorPlanErrors, [fieldName]: errorMessage });

        return true;
      }
    }

    return false;
  };

  const save = async (
    event: string | { target: { name: string; value: string; checked?: boolean; type?: string } },
  ) => {
    setIsSaving(true);

    const target = typeof event === 'string' ? { name: event, value: '' } : event.target;

    const { name: fieldName, value, checked, type } = target;

    const isCheckbox = type === CHECKBOX;

    if (!isCheckbox) {
      if (hasErrorOrNoChange(target)) {
        setIsSaving(false);

        return;
      }
    }

    const valueToSave = isCheckbox ? checked : value;
    const fallbackValue = prevStateRef.current.value;

    const floorPlanId = floorPlan?.id;

    try {
      if (!floorPlanId) {
        logError({
          errorType: 'FloorPlanIdMissingError',
          error: new Error('Missing floorPlanId'),
          context: `FloorPlanModal.save: listingAlias: ${listingAlias}, fieldName: ${fieldName}, floor plans: ${JSON.stringify(floorplans)}, floorPlanIndex: ${floorPlanIndex}`,
        });
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }

      const mutation = fieldMutationMap[fieldName as keyof typeof fieldMutationMap];

      let apiValue: string | number | boolean | undefined;
      if (fieldName === 'active') {
        const boolValue = Boolean(valueToSave);
        setActive(boolValue);
        apiValue = boolValue;
      } else if (fieldName === 'name') {
        const stringValue = String(valueToSave);
        setName(stringValue);
        apiValue = stringValue;
      } else if (fieldName === 'beds') {
        const numValue = Number(valueToSave);
        setBeds(valueToSave as string | number);
        apiValue = numValue;
      } else if (fieldName === 'baths') {
        const numValue = Number(valueToSave);
        setBaths(valueToSave as string | number);
        apiValue = numValue;
        // squareFeet, lowPrice, highPrice - allow undefined for empty values
      } else if (fieldName === 'squareFeet') {
        setSquareFeet(valueToSave as string | number);
        apiValue = valueToSave ? Number(valueToSave) : undefined;
      } else if (fieldName === 'lowPrice') {
        setLowPrice(valueToSave as string | number);
        apiValue = valueToSave ? Number(valueToSave) : undefined;
      } else if (fieldName === 'highPrice') {
        setHighPrice(valueToSave as string | number);
        apiValue = valueToSave ? Number(valueToSave) : undefined;
      }

      await mutation.mutate({
        getParams: {
          floorPlanId,
          [mutation.param]: apiValue,
        },
      });

      if (!prevStateRef.current.isModified) {
        prevStateRef.current.isModified = true;
      }
    } catch (error) {
      if (fieldName === 'name') {
        setName(fallbackValue);
      } else if (fieldName === 'beds') {
        setBeds(fallbackValue as string | number);
      } else if (fieldName === 'baths') {
        setBaths(fallbackValue as string | number);
      } else if (fieldName === 'squareFeet') {
        setSquareFeet(fallbackValue as string | number);
      } else if (fieldName === 'lowPrice') {
        setLowPrice(fallbackValue as string | number);
      } else if (fieldName === 'highPrice') {
        setHighPrice(fallbackValue as string | number);
      } else if (fieldName === 'active') {
        setActive(fallbackValue === 'true');
      }

      const errorMessage = error instanceof Error ? error.message : String(error);

      setFloorPlanErrors({
        ...floorPlanErrors,
        [fieldName as string]: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseClick = () => {
    if (!isCloseClicked) {
      setIsCloseClicked(true);
    }
  };

  const handleFocus = (event: { target: { name: string; value: string } }) => {
    const { name: fieldName, value } = event.target;

    prevStateRef.current.value = value;
    prevStateRef.current.errorMessage = floorPlanErrors[fieldName as keyof FloorPlanErrors] as string;
  };

  const clearFieldError = (event: { target: { name: string } }) => {
    const fieldName = event.target.name;

    setFloorPlanErrors({ ...floorPlanErrors, [fieldName]: '' });
  };

  return (
    <Modal
      header={<Heading level={2}>Edit floor plan</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <RMWInput
              disabled={isInputFormDisabled}
              isRequired={!isInputFormDisabled}
              errorMessage={floorPlanErrors.name}
              fieldName="name"
              flex="1"
              initialValue={name}
              label="Floor plan name"
              clearFieldError={clearFieldError}
              onBlur={save}
              onFocus={handleFocus}
            />

            <Box css={{ flex: '1' }}>
              <FormField
                disabled={isInputFormDisabled}
                required={!isInputFormDisabled}
                label={<Label>Beds</Label>}
                error={Boolean(floorPlanErrors.beds)}
                control={
                  <Select name="beds" value={beds} onChange={save} onFocus={handleFocus}>
                    <option disabled={true} value="">
                      Select
                    </option>
                    {bedOptionsArray.map(({ label, value }) => (
                      <option key={label} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                }
                description={Boolean(floorPlanErrors.beds) && <FormHelp>{String(floorPlanErrors.beds)}</FormHelp>}
              />
            </Box>
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box css={{ flex: '1' }}>
              <FormField
                disabled={isInputFormDisabled}
                required={!isInputFormDisabled}
                label={<Label>Baths</Label>}
                error={Boolean(floorPlanErrors.baths)}
                control={
                  <Select name="baths" value={baths} onChange={save} onFocus={handleFocus}>
                    <option disabled={true} value="">
                      Select
                    </option>
                    {bathOptionsArray.map(({ label, value }) => (
                      <option key={label} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                }
                description={Boolean(floorPlanErrors.baths) && <FormHelp>{String(floorPlanErrors.baths)}</FormHelp>}
              />
            </Box>

            <RMWInput
              disabled={isInputFormDisabled}
              showOptional={canAccessTotalMonthlyPriceWithFee}
              errorMessage={floorPlanErrors.squareFeet}
              flex="1"
              fieldName="squareFeet"
              initialValue={String(squareFeet)}
              label="Square footage"
              clearFieldError={clearFieldError}
              onBlur={save}
              onFocus={handleFocus}
            />
          </Box>

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <RMWInput
              disabled={isInputFormDisabled || isPartialListingManaged}
              readOnly={floorPlan?.units && floorPlan.units.length > 0}
              showOptional={canAccessTotalMonthlyPriceWithFee}
              description={`Floor plans without ${canAccessTotalMonthlyPriceWithFee ? 'base rent' : 'prices'} will not appear on Zillow`}
              errorMessage={floorPlanErrors.lowPrice}
              fieldName="lowPrice"
              flex="1"
              initialValue={String(lowPrice)}
              inputType="adorned"
              label={canAccessTotalMonthlyPriceWithFee ? 'Min base rent' : 'Min price'}
              leftAdornment="$"
              rightAdornment="/mo"
              clearFieldError={clearFieldError}
              onBlur={save}
              onFocus={handleFocus}
            />

            <RMWInput
              disabled={isInputFormDisabled || isPartialListingManaged}
              readOnly={floorPlan?.units && floorPlan.units.length > 0}
              showOptional={canAccessTotalMonthlyPriceWithFee}
              errorMessage={floorPlanErrors.highPrice}
              fieldName="highPrice"
              flex="1"
              initialValue={String(highPrice)}
              inputType="adorned"
              label={canAccessTotalMonthlyPriceWithFee ? 'Max base rent' : 'Max price'}
              leftAdornment="$"
              rightAdornment="/mo"
              clearFieldError={clearFieldError}
              onBlur={save}
              onFocus={handleFocus}
            />
          </Box>

          <InlineStaticAlert marginTop="200" />

          {isMultifamily && (
            <>
              {floorplans.length > 1 && (
                <div>
                  <LabeledControl
                    disabled={isInputFormDisabled || isPartialListingManaged}
                    aria-label="floor plan available"
                    size="lg"
                    label={<Label>Show as available</Label>}
                    labelPosition="left"
                    control={<Switch name="active" checked={active} onChange={save} onFocus={handleFocus} />}
                  />

                  {floorPlanErrors.active ? (
                    <FormHelp error={true}>{String(floorPlanErrors.active)}</FormHelp>
                  ) : (
                    <Box css={{ minHeight: '400' }} />
                  )}
                </div>
              )}

              <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
                <FloorPlanLayout floorPlanIndex={floorPlanIndex} />

                <Box css={{ display: 'flex', flexDirection: 'column', gap: '200', flex: '1 1 50%' }}>
                  <Text textStyle="body-bold">3D tour</Text>
                  {!floorPlan?.vrModel?.vrModelId && !floorPlan?.externalTour?.tourUrl ? (
                    <TourCardEmpty
                      isDisabled={is3dTourDisabled}
                      isHidden={floorPlan?.units && floorPlan.units.length > 0}
                      listingToursCount={0}
                      tourType="floorPlan"
                      floorPlanIndex={floorPlanIndex}
                    />
                  ) : (
                    <TourCardEditable
                      floorPlanIndex={floorPlanIndex}
                      vrModel={{ ...floorPlan?.vrModel, creatorZuid: Number(floorPlan?.vrModel?.creatorZuid) }}
                      externalTour={floorPlan?.externalTour}
                      tourType="floorPlan"
                      hasBigBottomBorder={false}
                      isHidden={floorPlan?.units && floorPlan.units.length > 0}
                      isDisabled={is3dTourDisabled}
                      updateStatusMessage={(message: string, status = NotificationConstants.SUCCESS) =>
                        enqueueToast(<Toast tone={status}>{message}</Toast>)
                      }
                    />
                  )}
                </Box>
              </Box>

              <Box css={{ display: 'flex', flexDirection: 'column', gap: '200' }}>
                <Text textStyle="body-bold">Photos</Text>

                <FloorPlanUploadFormField floorPlanIndex={floorPlanIndex} />
              </Box>

              <FloorPlanAmenities floorPlanIndex={floorPlanIndex} />
            </>
          )}
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Edit floor plan actions">
          <Modal.Close>
            <TextButton>Cancel</TextButton>
          </Modal.Close>

          <Button
            emphasis="filled"
            onClick={handleCloseClick}
            loading={isSaving && isCloseClicked}
            disabled={!name || !beds || !baths}
          >
            Done
          </Button>
        </ButtonGroup>
      }
      useDismissProps={{ outsidePress: false }}
      open={true}
      onOpenChange={(open) => !open && handleCloseClick()}
      size={{ base: 'fullScreen', lg: 'md' }}
    />
  );
};

export default FloorPlanModal;
